import { Plus } from "lucide-react"
import { useMemo, useState } from "react"

import { Button } from "components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select"
import { ChecklistSummary } from "./types"
import type { CohortRoleLabelSource } from "utils/cohortRoleLabels"
import { getInviteeBucketLabel } from "utils/cohortRoleLabels"

type TodosCardProps = {
  summary: ChecklistSummary | null
  role: "mentors" | "scholars"
  onRoleChange: (role: "mentors" | "scholars") => void
  onCreate: () => void
  onTemplateClick: (templateId: string) => void
  loading: boolean
  error: string | null
  canCreate: boolean
  roleLabels?: CohortRoleLabelSource
  className?: string
}

export function TodosCard({
  summary,
  role,
  onRoleChange,
  onCreate,
  onTemplateClick,
  loading,
  error,
  canCreate,
  roleLabels,
  className,
}: TodosCardProps) {
  const roleSummary = summary?.[role]
  const totalUsers = roleSummary?.totalUsers ?? 0
  const templates = useMemo(() => roleSummary?.templates ?? [], [roleSummary])
  const [expandedTemplates, setExpandedTemplates] = useState<Record<string, boolean>>({})
  const toggleExpanded = (templateId: string) => {
    setExpandedTemplates((prev) => ({ ...prev, [templateId]: !prev[templateId] }))
  }
  const formatDueDate = (iso?: string | null) => {
    if (!iso) return ""
    const [year, month, day] = iso.split("T")[0].split("-").map(Number)
    if (!year || !month || !day) return iso
    const date = new Date(year, month - 1, day)
    if (Number.isNaN(date.getTime())) return iso
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
  }
  const formatCompletedDate = (iso?: string | null) => {
    if (!iso) return ""
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return iso
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
  }
  const sortedTemplates = useMemo(() => templates, [templates])
  const audienceOptions = (["scholars", "mentors"] as const).map((value) => ({
    value,
    label: getInviteeBucketLabel(value, roleLabels),
    count: summary?.[value]?.totalUsers ?? 0,
  }))

  return (
    <section className={`cohort-open-section min-w-0 ${className || ""}`} id="admin-checklist" aria-labelledby="admin-checklist-heading">
      <div className="cohort-card-heading cohort-card-heading--wrap">
        <h2 id="admin-checklist-heading" className="text-[19px] text-[var(--cohort-ink)] sm:text-xl">Checklist progress</h2>
        <div className="flex items-center gap-4">
          <Select value={role} onValueChange={(value) => onRoleChange(value as "mentors" | "scholars")}>
            <SelectTrigger aria-label="Checklist audience" className="cohort-secondary-button cohort-checklist-role-trigger !min-h-10 w-auto rounded-[10px] px-3.5 py-0 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="cohort-experience">
              {audienceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label} · {option.count}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="auto" onClick={onCreate} disabled={!canCreate} className="cohort-primary-button hidden min-h-10 px-4 py-2 text-sm sm:inline-flex">
            <Plus className="h-3 w-3" />
            New item
          </Button>
        </div>
      </div>
      {!canCreate ? <p className="py-3 text-sm text-[var(--cohort-ink-muted)]">Add a cohort start date before creating checklist items.</p> : null}
      <div>
        {loading && <div className="py-5 text-sm text-[var(--cohort-ink-muted)]">Loading checklist templates…</div>}
        {error && !loading && <div className="rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] p-3 text-sm text-[var(--cohort-clay)]">{error}</div>}
        {!loading && !error && templates.length === 0 ? (
          <div className="py-5 text-sm text-[var(--cohort-ink-muted)]">No checklist templates assigned yet.</div>
        ) : null}
        {!loading && !error && templates.length > 0 && (
          <div className="cohort-bounded-list w-full" role="region" aria-label="Checklist templates">
            {sortedTemplates.map((template) => {
              const completed = template.completedCount
              const percentage = totalUsers ? Math.round((completed / totalUsers) * 100) : 0
              const completionCountLabel = totalUsers
                ? `${completed} of ${totalUsers}`
                : "No members in this role yet"
              const dueLabel = template.dueDateVaries
                ? "Due date varies"
                : template.dueDate
                  ? `Due ${formatDueDate(template.dueDate)}`
                  : "Due date not set"
              return (
                <div
                  key={template.id}
                  className="cohort-ruled-row py-3.5 sm:py-[18px]"
                >
                  <div className="grid min-w-0 grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-x-6">
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-base font-medium leading-[1.4] text-[var(--cohort-ink)] sm:text-[17px]">{template.title}</p>
                      {template.description || dueLabel ? (
                        <p className="mt-1 break-words text-sm text-[var(--cohort-ink-muted)]">
                          {template.description ? <span className="hidden sm:inline">{template.description}{dueLabel ? " · " : ""}</span> : null}
                          {dueLabel}
                        </p>
                      ) : null}
                    </div>
                    <div className="mt-3 flex items-center gap-3.5 sm:col-span-2 sm:row-start-2 sm:mt-3.5 sm:gap-4">
                      <div className="h-[3px] flex-1 bg-[var(--cohort-rule)]">
                        <div
                          className="h-[3px] bg-[var(--cohort-forest)] transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="shrink-0 text-sm font-medium text-[var(--cohort-ink)] sm:text-[15px]">
                        {completionCountLabel}
                        {totalUsers ? <span className="hidden sm:inline"> · {percentage}%</span> : null}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-[22px] sm:col-start-2 sm:row-start-1 sm:mt-0 sm:flex-nowrap sm:items-center sm:gap-[18px]">
                      <button type="button" className="cohort-text-action min-h-11 text-sm" onClick={() => onTemplateClick(template.id)}>Edit</button>
                      <button type="button" aria-label={expandedTemplates[template.id] ? "Hide member progress" : "Show member progress"} className="cohort-text-action min-h-11 text-sm" onClick={() => toggleExpanded(template.id)}>
                        <span className="sm:hidden">{expandedTemplates[template.id] ? "Hide member progress" : "Member progress"}</span>
                        <span className="hidden sm:inline">{expandedTemplates[template.id] ? "Hide member progress" : "Show member progress"}</span>
                      </button>
                    </div>
                    {expandedTemplates[template.id] && (
                      <div className="cohort-bounded-list cohort-bounded-list--compact mt-3.5 border-l-2 border-[var(--cohort-rule)] pl-4 sm:col-span-2 sm:pl-5" role="region" aria-label={`${template.title} member progress`} tabIndex={0}>
                        {(template.users || []).map((user) => (
                          <div key={user.id} className="flex items-center justify-between gap-4 py-2 text-sm">
                            <span className="min-w-0 break-words text-[var(--cohort-ink)]">{user.name}</span>
                            <span className={`shrink-0 ${user.completed ? "text-[var(--cohort-success)]" : "text-[var(--cohort-ink-muted)]"}`}>
                              {user.completed
                                ? `Completed ${formatCompletedDate(user.completedAt)}`
                                : "Not completed"}
                            </span>
                          </div>
                        ))}
                        {(template.users || []).length === 0 && (
                          <p className="py-2 text-sm text-[var(--cohort-ink-muted)]">No users assigned.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
        <button type="button" onClick={onCreate} disabled={!canCreate} className="cohort-text-action min-h-11 text-[15px] disabled:cursor-not-allowed disabled:text-[var(--cohort-ink-muted)] sm:hidden">New checklist item</button>
      </div>
    </section>
  )
}
